import { monthEnd } from '@/lib/dates'
import { usd } from '@/lib/money'
import { cn } from '@/lib/utils'

const DOW = ['월', '화', '수', '목', '금', '토', '일']

export interface HeatDay {
  date: string
  revenue: number
  cost: number
}

/** The amount on a cell: cents matter (most days are under $1), so two
 *  decimals up to $999.99, then "$1.2k". */
export function cellAmount(v: number): string {
  if (v >= 1000) return `$${(v / 1000).toFixed(1)}k`
  return usd(v)
}

/** Four steps of the chart colour by the day's share of the month's best
 *  day. A day that earned nothing gets no fill. */
const STEPS = ['bg-chart-1/10', 'bg-chart-1/20', 'bg-chart-1/35', 'bg-chart-1/50'] as const

export function heatStep(revenue: number, max: number): 0 | 1 | 2 | 3 | 4 {
  if (!(revenue > 0) || !(max > 0)) return 0
  const t = revenue / max
  return t > 0.75 ? 4 : t > 0.5 ? 3 : t > 0.25 ? 2 : 1
}

export interface HeatCalendarProps {
  /** "2026-10". */
  month: string
  days: HeatDay[] | undefined
  today: string
  selected?: string | null
  onSelect: (date: string) => void
  /** Whether cost is known: a deficit dot on a day whose spend cannot come
   *  in would be a guess. */
  showDeficit?: boolean
  className?: string
}

/** A month as a Monday-first grid. Each cell is the day number and its
 *  revenue; the fill steps with revenue (four steps of the first chart
 *  colour), a dot marks a day that cost more than it earned, the ring marks
 *  today and the open day. Future days cannot be opened. */
export function HeatCalendar({ month, days, today, selected, onSelect, showDeficit = true, className }: HeatCalendarProps) {
  const [y, m] = month.split('-').map(Number)
  const last = Number(monthEnd(month).slice(8))
  const firstDow = (new Date(Date.UTC(y, m - 1, 1)).getUTCDay() + 6) % 7
  const byDate = new Map((days ?? []).map((d) => [d.date, d]))
  const max = Math.max(0, ...(days ?? []).map((d) => d.revenue))
  return (
    <div className={className}>
      <div className="mb-1.5 grid grid-cols-7 gap-1 sm:gap-1.5" aria-hidden>
        {DOW.map((d) => (
          <div key={d} className="text-center text-meta text-muted-foreground">
            {d}
          </div>
        ))}
      </div>
      <div role="group" aria-label={`${y}년 ${m}월`} className="grid grid-cols-7 gap-1 sm:gap-1.5">
        {Array.from({ length: firstDow }, (_, i) => (
          <div key={`pad-${i}`} />
        ))}
        {Array.from({ length: last }, (_, i) => {
          const day = i + 1
          const date = `${month}-${String(day).padStart(2, '0')}`
          const d = byDate.get(date)
          const future = date > today
          const on = date === selected
          const step = d ? heatStep(d.revenue, max) : 0
          // Today is still being counted (AdMob revenue lags the day's
          // spend), so a loss there is not a loss yet: no dot until it is over.
          const deficit = showDeficit && !!d && date !== today && d.cost > d.revenue
          const name = `${m}월 ${day}일${d ? ` 수익 ${usd(d.revenue)}${deficit ? ' 적자' : ''}` : ' 데이터 없음'}${date === today ? ' 오늘' : ''}`
          return (
            <button
              key={date}
              type="button"
              disabled={future}
              onClick={() => onSelect(date)}
              aria-pressed={on}
              aria-label={name}
              title={d ? `${date} · 수익 ${usd(d.revenue)} · 지출 ${usd(d.cost)}` : date}
              className={cn(
                'relative flex h-14 min-w-0 sm:h-[4.5rem] flex-col justify-between overflow-hidden rounded-md border p-1 text-left transition-colors sm:p-1.5',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
                step > 0 && STEPS[step - 1],
                future ? 'cursor-default border-transparent text-muted-foreground/50' : 'hover:border-foreground/30',
                on ? 'border-accent ring-1 ring-accent' : date === today ? 'border-foreground/50' : !future && 'border-border',
              )}
            >
              <span className={cn('text-meta tabular-nums', step === 0 && 'text-muted-foreground')}>{day}</span>
              {d && d.revenue > 0 && (
                <span className="self-end whitespace-nowrap text-[10px] font-medium leading-none tabular-nums sm:text-meta">{cellAmount(d.revenue)}</span>
              )}
              {deficit && <i aria-hidden className="absolute bottom-1 left-1 h-1.5 w-1.5 rounded-full bg-status-failed" />}
            </button>
          )
        })}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-end gap-x-3 gap-y-1 text-meta text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          적음
          {STEPS.map((c) => (
            <i key={c} aria-hidden className={cn('h-3 w-5 rounded-sm border border-border', c)} />
          ))}
          많음
        </span>
        {showDeficit && (
          <span className="inline-flex items-center gap-1.5">
            <i aria-hidden className="h-1.5 w-1.5 rounded-full bg-status-failed" />
            적자인 날
          </span>
        )}
      </div>
    </div>
  )
}
